/**
 * P3 Phase 3: Clinic Subscription Payment Success Page
 * 
 * NON-NEGOTIABLE RULES:
 * 1. Frontend NEVER assumes payment success based on redirect/query params
 * 2. Must poll backend for subscription status (max 60-90 seconds)
 * 3. UI updates ONLY after backend confirms subscription state
 * 4. Falls back to "payment being confirmed" after timeout
 * 
 * ARCHITECTURE:
 * - Reads session_id from URL query params
 * - First checks checkout session status to get clinic_id
 * - Then polls GET /api/subscription/clinic/{clinic_id} for subscription status
 * - Shows appropriate UI based on backend response ONLY
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useClinicData } from '../../context/ClinicDataContext';
import { useClinicScope } from '../../hooks/useClinicScope';
import StripeService from '../../services/StripeService';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { 
  CheckCircle, Loader2, AlertTriangle, Clock, 
  ArrowRight, RefreshCw, HelpCircle, Mail, Building2,
  Zap, Crown, Shield
} from 'lucide-react';
import confetti from 'canvas-confetti';

// Polling configuration (NON-NEGOTIABLE)
const POLLING_INTERVAL_MS = 2000;     // Poll every 2 seconds
const MAX_POLLING_TIME_MS = 75000;    // 75 second max (within 60-90s guideline)
const MAX_POLLING_ATTEMPTS = Math.ceil(MAX_POLLING_TIME_MS / POLLING_INTERVAL_MS);

// Status states
const STATUS = {
  INITIALIZING: 'initializing', // Getting clinic info from session
  POLLING: 'polling',           // Actively polling backend
  SUCCESS: 'success',           // Backend confirmed subscription active
  PENDING_CONFIRMATION: 'pending_confirmation', // Timeout - webhook may be delayed
  FAILED: 'failed',             // Payment failed
  ERROR: 'error',               // Technical error
};

const PackageIcon = ({ packageId }) => {
  const icons = { STARTER: Zap, PRO: Crown, ENTERPRISE: Building2 };
  const Icon = icons[packageId] || Zap;
  return <Icon className="h-8 w-8" />;
};

const ClinicPaymentSuccess = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { clinicUser } = useClinicData();
  const { clinicId: rbacClinicId } = useClinicScope();
  
  // State
  const [status, setStatus] = useState(STATUS.INITIALIZING);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  const [error, setError] = useState(null);
  const [resolvedClinicId, setResolvedClinicId] = useState(null);
  
  // Refs for cleanup
  const pollingRef = useRef(null);
  const startTimeRef = useRef(null);
  const reconcileAttemptedRef = useRef(false);
  
  // Get params from URL
  const sessionId = searchParams.get('session_id');
  const isUpgrade = searchParams.get('upgrade') === 'true';
  const expectedPkgKey = searchParams.get('pkg'); // P0-A FIX: Expected package from upgrade URL
  
  /**
   * Get clinic ID from checkout session if not available from context
   */
  const resolveClinicId = useCallback(async () => {
    // First try from RBAC context, then clinicUser context
    if (rbacClinicId) {
      return rbacClinicId;
    }
    if (clinicUser?.clinicId) {
      return clinicUser.clinicId;
    }
    
    // If no clinicUser, try to get from session status
    if (sessionId) {
      try {
        const checkoutStatus = await StripeService.getCheckoutStatus(sessionId);
        if (checkoutStatus.clinicId) {
          return checkoutStatus.clinicId;
        }
      } catch (err) {
        console.error('[ClinicPaymentSuccess] Failed to get clinic from session:', err);
      }
    }
    
    return null;
  }, [rbacClinicId, clinicUser, sessionId]);
  
  /**
   * Poll backend for subscription status
   * This is the ONLY source of truth for payment status
   * P0-1 FIX: After reconciliation, read the SPECIFIC session's activation result
   */
  const pollSubscriptionStatus = useCallback(async (clinicId) => {
    if (!clinicId) {
      console.error('[ClinicPaymentSuccess] No clinicId available for polling');
      return false;
    }
    
    try {
      const response = await StripeService.getClinicSubscription(clinicId);
      
      // Backend confirms active subscription
      if (response.hasSubscription && response.status === 'ACTIVE') {
        // P0-A FIX: If we know the expected package, verify the activated plan matches
        const confirmedData = { ...response };
        if (expectedPkgKey && response.packageId !== expectedPkgKey) {
          // The active subscription is for a DIFFERENT plan — upgrade webhook
          // may not have fired yet. Keep polling.
          console.log(`[ClinicPaymentSuccess] Active plan is ${response.packageId}, expected ${expectedPkgKey}. Continuing poll.`);
          // Don't give up immediately — try reconciliation first
        } else {
          setSubscriptionData(confirmedData);
          setStatus(STATUS.SUCCESS);
          if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
          confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 } });
          return true;
        }
      }
      
      // V-01: reconciliation only activates on PSP-VERIFIED settlement; it can
      // never confirm a payment from local state. Success is still decided by
      // the canonical subscription read below.
      if (sessionId && !reconcileAttemptedRef.current) {
        try {
          reconcileAttemptedRef.current = true;
          console.log('[ClinicPaymentSuccess] Triggering PSP-verified reconciliation...');
          const reconcileResult = await StripeService.reconcileSession(sessionId);
          console.log('[ClinicPaymentSuccess] Reconcile result:', reconcileResult);
          
          // Re-check subscription after reconciliation
          const retryResponse = await StripeService.getClinicSubscription(clinicId);
          if (retryResponse.hasSubscription && retryResponse.status === 'ACTIVE') {
            // After reconciliation, accept the plan (it should be correct now)
            setSubscriptionData(retryResponse);
            setStatus(STATUS.SUCCESS);
            if (pollingRef.current) {
              clearInterval(pollingRef.current);
              pollingRef.current = null;
            }
            confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 } });
            return true;
          }
        } catch (reconcileErr) {
          console.warn('[ClinicPaymentSuccess] Reconciliation attempt:', reconcileErr?.message);
        }
      }
      
      return false; // Keep polling
      
    } catch (err) {
      console.error('[ClinicPaymentSuccess] Polling error:', err);
      return false;
    }
  }, [sessionId, expectedPkgKey]);
  
  /**
   * Initialize and start polling
   */
  useEffect(() => {
    if (!sessionId) {
      setStatus(STATUS.ERROR);
      setError(t('payment.noSession', 'No payment session found. Please try again.'));
      return;
    }
    
    const initializePolling = async () => {
      // Resolve clinic ID
      const clinicId = await resolveClinicId();
      
      if (!clinicId) {
        // Wait a bit more for context to load
        setTimeout(async () => {
          const retryClinicId = await resolveClinicId();
          if (!retryClinicId) {
            setStatus(STATUS.ERROR);
            setError(t('payment.unableIdentifyClinic', 'Unable to identify clinic. Please log in and try again.'));
          } else {
            setResolvedClinicId(retryClinicId);
            startPolling(retryClinicId);
          }
        }, 2000);
        return;
      }
      
      setResolvedClinicId(clinicId);
      startPolling(clinicId);
    };
    
    const startPolling = (clinicId) => {
      setStatus(STATUS.POLLING);
      startTimeRef.current = Date.now();
      
      // Start polling
      const poll = async () => {
        const elapsedTime = Date.now() - startTimeRef.current;
        const currentAttempt = Math.ceil(elapsedTime / POLLING_INTERVAL_MS);
        
        setPollCount(currentAttempt);
        
        // Check timeout
        if (elapsedTime >= MAX_POLLING_TIME_MS) {
          if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
          // V-01: a final PSP-verified reconciliation attempt; if the provider
          // cannot confirm settlement we stay in PENDING_CONFIRMATION.
          if (sessionId) {
            try {
              await StripeService.reconcileSession(sessionId);
              // One final poll after reconciliation
              const reconciled = await pollSubscriptionStatus(clinicId);
              if (reconciled) return;
            } catch (reconcileErr) {
              console.warn('[ClinicPaymentSuccess] Timeout reconciliation:', reconcileErr?.message);
            }
          }
          setStatus(STATUS.PENDING_CONFIRMATION);
          return;
        }
        
        await pollSubscriptionStatus(clinicId);
      };
      
      // Initial poll
      poll();
      
      // Set up interval
      pollingRef.current = setInterval(poll, POLLING_INTERVAL_MS);
    };
    
    initializePolling();
    
    // Cleanup — aggressively reset confetti on unmount to prevent leak to other pages
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      // Force-remove all confetti particles and the global canvas
      try { confetti.reset(); } catch (_) {}
      // BUGFIX: Remove confetti canvas elements from DOM to prevent leak to other pages
      document.querySelectorAll('canvas').forEach(c => {
        if (c.style.position === 'fixed' && c.style.pointerEvents === 'none') {
          c.remove();
        }
      });
    };
  }, [sessionId, resolveClinicId, pollSubscriptionStatus]);
  
  /**
   * Manual retry after timeout — triggers reconciliation first
   */
  const handleRetry = async () => {
    if (!resolvedClinicId) {
      const clinicId = await resolveClinicId();
      if (!clinicId) {
        setError(t('payment.unableIdentifyClinic', 'Unable to identify clinic. Please log in and try again.'));
        return;
      }
      setResolvedClinicId(clinicId);
    }
    
    setStatus(STATUS.POLLING);
    startTimeRef.current = Date.now();
    setPollCount(0);
    
    // Trigger reconciliation first (in case webhook was missed)
    if (sessionId) {
      try {
        await StripeService.reconcileSession(sessionId);
      } catch (reconcileErr) {
        console.warn('[ClinicPaymentSuccess] Reconciliation attempt:', reconcileErr?.message);
      }
    }
    
    // Then poll for the result
    const confirmed = await pollSubscriptionStatus(resolvedClinicId);
    if (confirmed) {
      return;
    }
    
    // Resume polling
    pollingRef.current = setInterval(async () => {
      const elapsedTime = Date.now() - startTimeRef.current;
      setPollCount(Math.ceil(elapsedTime / POLLING_INTERVAL_MS));
      
      if (elapsedTime >= MAX_POLLING_TIME_MS) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
        setStatus(STATUS.PENDING_CONFIRMATION);
        return;
      }
      
      await pollSubscriptionStatus(resolvedClinicId);
    }, POLLING_INTERVAL_MS);
  };
  
  // Progress percentage for polling
  const pollProgress = Math.min(100, (pollCount / MAX_POLLING_ATTEMPTS) * 100);
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center p-4">
      <div className="max-w-lg w-full">
        
        {/* INITIALIZING STATE */}
        {status === STATUS.INITIALIZING && (
          <Card className="bg-white shadow-xl border-0" data-testid="payment-initializing">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 mx-auto mb-6 bg-blue-100 rounded-full flex items-center justify-center">
                <Loader2 className="h-10 w-10 text-blue-600 animate-spin" />
              </div>
              
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                {t('payment.initializing', 'Processing...')}
              </h1>
              
              <p className="text-gray-600">
                {t('payment.loadingSession', 'Loading your payment session...')}
              </p>
            </CardContent>
          </Card>
        )}
        
        {/* POLLING STATE */}
        {status === STATUS.POLLING && (
          <Card className="bg-white shadow-xl border-0" data-testid="payment-polling">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 mx-auto mb-6 bg-blue-100 rounded-full flex items-center justify-center">
                <Loader2 className="h-10 w-10 text-blue-600 animate-spin" />
              </div>
              
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                {t('payment.verifyingPayment', 'Verifying Payment...')}
              </h1>
              
              <p className="text-gray-600 mb-6">
                {expectedPkgKey 
                  ? t('payment.pleaseWaitPkg', 'Confirming your {{plan}} subscription...', { plan: expectedPkgKey })
                  : t('payment.pleaseWait', 'Please wait while we confirm your subscription.')}
              </p>
              
              {/* Progress bar */}
              <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${pollProgress}%` }}
                />
              </div>
              
              <p className="text-sm text-gray-500">
                {t('payment.checkingStatus', 'Checking payment status...')} ({Math.round(pollProgress)}%)
              </p>
              
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
                  <Shield className="h-4 w-4" />
                  <span>{t('payment.secureVerification', 'Secure verification in progress')}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* SUCCESS STATE */}
        {status === STATUS.SUCCESS && subscriptionData && (
          <Card className="bg-white shadow-xl border-0" data-testid="payment-success">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="h-12 w-12 text-green-600" />
              </div>
              
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                {isUpgrade 
                  ? t('payment.upgradeComplete', 'Upgrade Complete!')
                  : t('payment.subscriptionActive', 'Subscription Active!')
                }
              </h1>
              
              <p className="text-gray-600 mb-6">
                {t('payment.welcomeToPlan', 'Welcome to {{plan}}!', { 
                  plan: subscriptionData.packageName 
                })}
              </p>
              
              {/* Subscription details */}
              <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-6 mb-6">
                <div className="flex items-center justify-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-blue-600 text-white rounded-xl flex items-center justify-center">
                    <PackageIcon packageId={subscriptionData.packageId} />
                  </div>
                  <div className="text-left">
                    <h3 className="font-bold text-gray-900">{subscriptionData.packageName}</h3>
                    <Badge className="bg-green-100 text-green-700">
                      {t('payment.active', 'Active')}
                    </Badge>
                  </div>
                </div>
                
                {subscriptionData.amount > 0 && (
                  <p className="text-sm text-gray-600">
                    {(() => {
                      try {
                        return new Intl.NumberFormat(undefined, { style: 'currency', currency: (subscriptionData.currency || 'TRY').toUpperCase() }).format(subscriptionData.amount);
                      } catch {
                        return `${(subscriptionData.currency || '').toUpperCase()} ${subscriptionData.amount}`;
                      }
                    })()}/{t('common.month', 'month')}
                  </p>
                )}
              </div>
              
              {/* Next steps */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
                <h4 className="font-semibold text-gray-900 mb-3">
                  {t('payment.whatsNext', "What's Next?")}
                </h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>{t('payment.confirmationEmail', 'Confirmation email sent to your inbox')}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>{t('payment.fullAccess', 'Full access to all plan features')}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>{t('payment.manageSubscription', 'Manage subscription from dashboard')}</span>
                  </li>
                </ul>
              </div>
              
              <Button 
                onClick={() => navigate('/clinic/admin')}
                className="w-full bg-blue-600 hover:bg-blue-700"
                size="lg"
                data-testid="go-to-dashboard-btn"
              >
                {t('payment.goToDashboard', 'Go to Dashboard')}
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}
        
        {/* PENDING CONFIRMATION STATE (Timeout) */}
        {status === STATUS.PENDING_CONFIRMATION && (
          <Card className="bg-white shadow-xl border-0" data-testid="payment-pending">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 mx-auto mb-6 bg-amber-100 rounded-full flex items-center justify-center">
                <Clock className="h-10 w-10 text-amber-600" />
              </div>
              
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                {t('payment.confirmingPayment', 'Payment Being Confirmed')}
              </h1>
              
              <p className="text-gray-600 mb-6">
                {t('payment.pendingMessage', 'Your payment is being processed. This may take a few minutes.')}
              </p>
              
              {/* Info box */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 text-left">
                <div className="flex items-start gap-3">
                  <HelpCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">
                      {t('payment.whyPending', 'Why is this happening?')}
                    </h4>
                    <p className="text-sm text-gray-600">
                      {t('payment.pendingExplanation', 'Payment confirmations can sometimes take a few minutes. Your subscription will be activated as soon as we receive confirmation from our payment provider.')}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Actions */}
              <div className="space-y-3">
                <Button 
                  onClick={handleRetry}
                  variant="outline"
                  className="w-full"
                  data-testid="retry-verification-btn"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  {t('payment.checkAgain', 'Check Again')}
                </Button>
                
                <Button 
                  onClick={() => navigate('/clinic/admin')}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  data-testid="continue-to-dashboard-btn"
                >
                  {t('payment.continueToDashboard', 'Continue to Dashboard')}
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
              
              {/* Support */}
              <div className="mt-6 pt-6 border-t text-sm text-gray-500">
                <p className="mb-2">{t('payment.needHelp', 'Need help?')}</p>
                <a 
                  href="mailto:support@treatx.health" 
                  className="text-blue-600 hover:underline flex items-center justify-center gap-1"
                >
                  <Mail className="h-4 w-4" />
                  support@treatx.health
                </a>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* ERROR STATE */}
        {status === STATUS.ERROR && (
          <Card className="bg-white shadow-xl border-0" data-testid="payment-error">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 mx-auto mb-6 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="h-10 w-10 text-red-600" />
              </div>
              
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                {t('payment.verificationError', 'Verification Error')}
              </h1>
              
              <p className="text-gray-600 mb-6">
                {error || t('payment.genericError', 'An error occurred while verifying your payment.')}
              </p>
              
              <div className="space-y-3">
                <Button 
                  onClick={() => navigate('/clinic/packages')}
                  className="w-full"
                  data-testid="try-again-btn"
                >
                  {t('payment.tryAgain', 'Try Again')}
                </Button>
                
                <Button 
                  onClick={() => navigate('/clinic/admin')}
                  variant="outline"
                  className="w-full"
                >
                  {t('payment.backToDashboard', 'Back to Dashboard')}
                </Button>
              </div>
              
              {/* Support */}
              <div className="mt-6 pt-6 border-t text-sm text-gray-500">
                <p className="mb-2">{t('payment.contactSupport', 'Contact support if the issue persists')}</p>
                <a 
                  href="mailto:support@treatx.health" 
                  className="text-blue-600 hover:underline flex items-center justify-center gap-1"
                >
                  <Mail className="h-4 w-4" />
                  support@treatx.health
                </a>
              </div>
            </CardContent>
          </Card>
        )}
        
      </div>
    </div>
  );
};

export default ClinicPaymentSuccess;
